import type { ChangeEvent, FocusEvent } from 'react';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import type { TextStyleValue } from '@elfsight/configurator-contracts';
import { uuid } from '@elfsight/lib-utils';
import type { TextControlBaseInstance } from '@elfsight/widget-ui';
import { TextControl } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { shuffle, without } from 'es-toolkit';
import type { ChoiceFieldValue, Field, Option } from '../../contexts';
import { useFormDispatch } from '../../contexts';
import { commonFieldsSettings } from '../commonFieldSettings';
import { ChoiceOption } from './choice-option';
import { choiceSettings } from './choice-settings';

export type ChoiceProps = Omit<Field, 'value'> & {
  className?: string;
  id?: string;
  value?: ChoiceFieldValue;
  initialValue?: ChoiceFieldValue;
  initialValueMultiple?: ChoiceFieldValue;
  checked?: boolean;
  label?: string;
  required?: boolean;
  showLabel?: boolean;
  multiple?: boolean;
  options?: Option[];
  otherOption?: boolean;
  otherOptionLabel?: string;
  otherOptionPlaceholder?: string;
  randomize?: boolean;
  numColumns?: number;
  fieldActiveColor?: string;
  fieldActiveFillColor?: string;
  fieldBorderType?: 'outline' | 'underline' | null;
  fieldBorderColor?: string;
  fieldBorderWidth?: number;
  fieldBorderRadius?: number;
  fieldFillColor?: string;

  fieldLabelPosition?: string;
  fieldLabelColor?: string;
  fieldLabelFontStyle?: TextStyleValue;

  fieldColor?: string;
  fieldFontStyle?: TextStyleValue;

  fieldSpacing?: number;
  onFocus?: (e: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onChange?: (
    value: ChoiceFieldValue | undefined,
    event?: ChangeEvent<HTMLInputElement>
  ) => void;
};

export const Choice = forwardRef<HTMLInputElement, ChoiceProps>(function Choice(
  {
    className,
    value,
    initialValue,
    initialValueMultiple,
    id,
    checked,
    required = false,
    multiple,
    options = [],
    randomize,
    numColumns = 1,

    otherOption,
    otherOptionLabel = choiceSettings.otherOptionLabel,
    otherOptionPlaceholder = choiceSettings.otherOptionPlaceholder,

    fieldActiveColor,
    fieldActiveFillColor,
    fieldBorderType,
    fieldBorderColor,
    fieldBorderWidth,
    fieldBorderRadius,
    fieldFillColor = commonFieldsSettings.fieldFillColor,

    fieldLabelColor = commonFieldsSettings.fieldLabelColor,
    fieldLabelFontStyle = commonFieldsSettings.fieldLabelFontStyle,

    fieldColor = commonFieldsSettings.fieldColor,
    fieldFontStyle = commonFieldsSettings.fieldFontStyle,

    fieldSpacing = commonFieldsSettings.fieldSpacing,
    onFocus,
    onChange,
    ...forwardingProps
  },
  forwardingRef
) {
  const { fontSize: fieldFontSize } = fieldFontStyle;

  const [otherOptionValue, setOtherOptionValue] = useState('');
  const [otherChecked, setOtherChecked] = useState(false);
  const otherOptionRef = useRef<HTMLInputElement | null>(null);
  const otherOptionInputRef = useRef<TextControlBaseInstance | null>(null);
  const dispatch = useFormDispatch();

  const orderIndexes = useMemo(() => {
    const orders = options.map((_, i) => i);
    return randomize ? shuffle(orders) : orders;
  }, [randomize, options.length]);

  const orderedOptions = useMemo(
    () => orderIndexes.map((i) => options[i]),
    [orderIndexes, options]
  );

  const currentInitialValue = useMemo(
    () => (multiple ? initialValueMultiple : initialValue),
    [multiple, initialValueMultiple, initialValue]
  );

  useEffect(() => {
    if (currentInitialValue) {
      dispatch({
        type: 'changeField',
        field: {
          id,
          value: currentInitialValue ?? undefined,
          valid: true,
          type: 'choice'
        }
      });
    }
  }, [currentInitialValue]);

  useEffect(() => {
    if (!otherOptionInputRef.current) {
      return;
    }

    if (otherChecked) {
      otherOptionInputRef.current.focusInput?.();
    }
  }, [otherChecked]);

  const handleCheck = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked, id } = event.target;

    const checkedOtherOption = !options.some(
      ({ id: optionID }) => optionID === id
    );

    let resultValue: ChoiceFieldValue | undefined = checkedOtherOption
      ? otherOptionValue
      : id;

    if (multiple && value) {
      if (checked) {
        resultValue = Array.isArray(value)
          ? value.concat(resultValue)
          : [value].concat(resultValue);
      } else {
        resultValue = Array.isArray(value)
          ? without(value, resultValue)
          : resultValue !== value
            ? value
            : undefined;
      }
    }

    if (onChange) {
      onChange(resultValue, event);
    }

    if (!otherOptionRef.current) {
      return;
    }

    setOtherChecked(otherOptionRef.current.checked);
  };

  const handleOtherOptionChange = (otherValue: string) => {
    let resultValue: ChoiceFieldValue = otherValue;

    if (multiple && value) {
      resultValue = Array.isArray(value)
        ? without(value, otherOptionValue).concat(resultValue)
        : without([value], otherOptionValue).concat(resultValue);
    }

    setOtherOptionValue(otherValue);

    if (onChange) {
      onChange(resultValue);
    }
  };

  const choiceOtherOptionLabel = otherOptionLabel.trim()
    ? otherOptionLabel
    : choiceSettings.otherOptionLabel;

  const choiceOptionProps = {
    ref: forwardingRef,
    name: id,
    required,
    inputType: multiple ? 'checkbox' : 'radio',
    fieldActiveColor,
    fieldActiveFillColor,
    fieldFillColor,
    fieldColor,
    fieldFontSize,
    fieldBorderType,
    fieldBorderColor,
    fieldBorderWidth,
    fieldSpacing,
    onFocus,
    onChange: handleCheck
  };

  return (
    <Container className={className}>
      <OptionsContainer
        style={{
          ...fieldFontStyle,
          color: fieldColor,
          gridTemplateColumns: `${100 / numColumns}% `.repeat(numColumns)
        }}
        {...{
          role: multiple ? undefined : 'radiogroup',
          'aria-required': multiple ? undefined : required
        }}
      >
        {orderedOptions.map((option: Option) => (
          <ChoiceOption
            key={option.id}
            id={option.id}
            {...choiceOptionProps}
            value={option.name}
            checked={
              checked !== undefined
                ? checked
                : Array.isArray(value)
                  ? value.includes(option.id)
                  : value === option.id
            }
          />
        ))}
        {otherOption && (
          <ChoiceOption
            key="other-option"
            {...choiceOptionProps}
            id={uuid()}
            ref={otherOptionRef}
            value={choiceOtherOptionLabel}
            checked={otherChecked}
          />
        )}
      </OptionsContainer>
      {otherOption && (
        <TextInput
          {...forwardingProps}
          ref={otherOptionInputRef}
          ariaLabel='Type your "other" answer'
          _checked={otherChecked}
          type="text"
          placeholder={otherOptionPlaceholder}
          labelColor={fieldLabelColor}
          labelFont={fieldLabelFontStyle}
          background={fieldFillColor}
          borderType={fieldBorderType ? fieldBorderType : undefined}
          borderColor={fieldBorderColor}
          borderWidth={fieldBorderWidth}
          borderRadius={fieldBorderRadius}
          font={fieldFontStyle}
          color={fieldColor}
          onValueChange={handleOtherOptionChange}
          onFocus={onFocus}
        />
      )}
    </Container>
  );
});

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'choice-container'
  })
})`
  height: max-content;
`;

const OptionsContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'choice-options-container'
  })
})`
  display: grid;
`;

const TextInput = styled(TextControl).attrs<{
  _checked: boolean;
}>({
  className: createReadableClass({
    component: 'fields',
    element: 'choice-other-option'
  })
})`
  display: ${({ _checked }) => (_checked ? 'block' : 'none')};
`;
